import { IImageComponentResponse } from "@customTypes/componentResponses";
import useAppContext from "@hooks/useAppContext";
import dynamic from "next/dynamic";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import ImageCloudinary from "./skeleton/ImageCloudinary";

const BackButton = dynamic(async () => await import("./icons/BackButton"));
interface IViewGalleryModalProps {
  viewGallery: boolean;
  setOpenGallery: Dispatch<SetStateAction<boolean>>;
  allImages: IImageComponentResponse[];
  title?: string;
}

function ViewGalleryModal(props: IViewGalleryModalProps): JSX.Element {
  const [photoIndex, setPhotoIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const { isMobile } = useAppContext();

  const images = useMemo(() => {
    return props.allImages;
  }, [props.allImages]);

  const reduceSize = (
    image: IImageComponentResponse,
    isSmall?: boolean
  ): { newWidth: number; newHeight: number } => {
    // Hardcoded reducing images, to be fixed when hook/function for calculating screen size is implemented;

    const CONTAINER_WIDTH = {
      desktop: {
        small: 366,
        big: 740,
      },
      mobile: {
        small: 175,
        big: 350,
      },
    };

    const divider =
      CONTAINER_WIDTH[isMobile ? "mobile" : "desktop"][
        isSmall ? "small" : "big"
      ];

    const scaleRatio = image.width! / divider;
    const newWidth = Math.ceil(image.width! / scaleRatio);
    const newHeight = Math.ceil(image.height! / scaleRatio);

    return { newWidth, newHeight };
  };

  return (
    <>
      <div className="fixed top-0 z-[100] mx-auto mb-3 max-h-[56.25rem] min-h-[100vh] w-[100%] overflow-y-auto rounded-2xl bg-white">
        <div className="md:inset-0 fixed top-0 left-0 right-0 z-[51] h-[100%] w-full overflow-y-auto overflow-x-hidden">
          <div className="relative h-screen w-full bg-white">
            {/* <!-- Modal content --> */}
            <div className="shadow dark:bg-gray-700 relative rounded-lg bg-white desktop:w-full ">
              {/* <!-- Modal header --> */}
              <div className=" sticky top-0 z-10 mx-auto flex max-w-[46.3125rem] items-start justify-between bg-white p-5 desktop:px-0">
                <div className="relative flex h-[2.75rem] w-full items-center justify-center">
                  <div className="absolute top-0 left-0 ">
                    <BackButton onClick={() => props.setOpenGallery(false)} />
                  </div>
                  <div className="px-12 text-center font-medium desktop:text-h6">
                    <h2>{props.title}</h2>
                  </div>
                </div>
              </div>
              {/* <!-- Modal body --> */}
              <div className="space-y-6">
                <section className="text-gray-700 overflow-hidden bg-white">
                  <div className="lg:pt-24 lg:px-32 container mx-auto px-6 py-2">
                    <div className="md:-m-2 -m-1 flex flex-col flex-wrap items-center justify-center">
                      <div className="flex w-[100%] max-w-[46.25rem] flex-wrap">
                        {images.map((image, index) => {
                          if (index % 3 === 0) {
                            const { newWidth, newHeight } = reduceSize(image);
                            return (
                              <div
                                key={image?.url || index}
                                className="relative block aspect-video h-fit w-full"
                              >
                                <ImageCloudinary
                                  className="block h-full w-full rounded-lg object-cover object-center"
                                  src={image.url}
                                  width={newWidth}
                                  height={newHeight}
                                  layout="fill"
                                  alt={props.title}
                                  objectFit="cover"
                                  onClick={() => {
                                    setIsOpen(true);
                                    setPhotoIndex(index);
                                  }}
                                />
                              </div>
                            );
                          } else {
                            const { newWidth, newHeight } = reduceSize(
                              image,
                              true
                            );
                            return (
                              <div
                                key={image?.url || index}
                                className={`relative block ${
                                  index % 3 === 1 ? "mr-1" : "ml-1"
                                } my-2 aspect-video h-fit desktop:mb-2`}
                                style={{ width: "calc(50% - 0.25rem)" }}
                              >
                                <ImageCloudinary
                                  className="block h-full w-full rounded-lg object-cover object-center"
                                  src={image.url}
                                  width={newWidth}
                                  height={newHeight}
                                  layout="fill"
                                  alt={props.title}
                                  objectFit="cover"
                                  onClick={() => {
                                    setIsOpen(true);
                                    setPhotoIndex(index);
                                  }}
                                />
                              </div>
                            );
                          }
                        })}
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isOpen && (
        <Lightbox
          index={photoIndex}
          slides={images.map((image) => ({ src: image.url }))}
          plugins={[Zoom]}
          open={photoIndex >= 0}
          close={() => {
            setPhotoIndex(-1);
            setIsOpen(false);
          }}
          zoom={{
            scrollToZoom: true,
            maxZoomPixelRatio: 1,
          }}
        />
      )}
    </>
  );
}

export default ViewGalleryModal;
